<template>
    <div class="container mx-auto h-12">
        <!-- eslint-disable-next-line tailwindcss/no-custom-classname -->
        <div class="relative w-full py-2 pl-14 opacity-0 transition-opacity" :class="{ 'opacity-100': swiperInstance, 'left-arrow-hidden': isNavigating && swiperInstance && activeSlide === 'category-0' }">
            <CategorySearch />
            <div class="h-12 w-full self-start py-3">
                <Swiper
                    v-if="Object.keys(shop().filteredCategories).length"
                    slides-per-view="auto"
                    :space-between="32"
                    :mousewheel="true"
                    :navigation="true"
                    :modules="[Navigation, Mousewheel]"
                    @swiper="onSwiper">
                    <template v-for="(category, index) in shop().filteredCategories" :key="category.id">
                        <SwiperSlide>
                            <nuxt-link
                                class="font-bold transition-all"
                                :class="{ 'text-primary': activeSlide === `category-${index}`, 'hover:opacity-60': activeSlide !== `category-${index}` }"
                                :to="{ hash: index > 0 ? `#category-${index}` : '' }"
                                @click.prevent="goToSlide(index, true)">
                                {{ category.title }}
                            </nuxt-link>
                        </SwiperSlide>
                    </template>
                </Swiper>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { Mousewheel, Navigation } from 'swiper/modules'
    import { Swiper, SwiperSlide } from 'swiper/vue'
    import CategorySearch from '~/components/Category/CategorySearch.vue'
    import { shop } from '~/store/shop'
    import 'swiper/css'
    const swiperInstance = ref<typeof Swiper>()
    const route = useRoute()
    const activeSlide = ref(route.hash ? route.hash : '#category-0')
    const isScrolling: boolean | ReturnType<typeof setTimeout> = ref(false)
    const manualScroll = ref(false)
    const { $listen, $destroy, $event } = useNuxtApp()
    const isNavigating = ref(false)
    watch(() => route.path, () => {
        isNavigating.value = true
    })
    onBeforeUnmount(() => {
        $destroy('category-list-in-viewport')
        $destroy('category-list-end-scroll')
    })
    onMounted(() => {
        $event('mounted-swiper')
        if (activeSlide.value !== 'category-0') {
            goToSlide(Number.parseInt(activeSlide.value.replace('#category-', '') as string, 10), true, 0)
        }
        window.onpopstate = function () {
            if (window.location.pathname === route.path) {
                const hashId = window.location.hash.includes('#category') ? window.location.hash.replace('#category-', '') : '0'
                goToSlide(Number.parseInt(hashId.replace('category-', '') as string, 10), true, 0)
            } else {
                manualScroll.value = true
            }
        }
        $listen('category-list-in-viewport', data => {
            if (!manualScroll.value) {
                goToSlide(Number.parseInt(data.id.replace('category-', '') as string, 10))
            }
        })
        $listen('category-list-end-scroll', () => {
            if (manualScroll.value) {
                endScroll()
            }
        })
    })
    function goToSlide (position: number, manual: boolean = false, speed: number = 300) {
        if (activeSlide.value !== `category-${position}`) {
            activeSlide.value = `category-${position}`
            if (manual) {
                manualScroll.value = true
            }
            if (swiperInstance.value) {
                swiperInstance.value.slideTo(position - 1, speed)
            }
        }
    }
    function endScroll () {
        clearTimeout(isScrolling.value)
        isScrolling.value = setTimeout(() => {
            manualScroll.value = false
        }, 50)
    }
    function onSwiper (swiper) {
        swiperInstance.value = swiper
    }
</script>

<style lang="scss">
    .swiper {
        // CSS Grid/Flexbox bug size workaround
        // @see https://github.com/kenwheeler/slick/issues/982
        @apply relative max-h-screen min-h-0 w-full min-w-0 max-w-full;
    }
    .swiper-slide {
        @apply w-auto;
    }
    .swiper-button-disabled {
        @apply hidden;
    }
    .swiper-button-prev,
    .swiper-button-next {
        @apply pointer-events-none absolute top-0 z-50 h-6 w-12 p-0 hover:before:opacity-80;
        &::before {
            @apply pointer-events-auto block h-6 w-6 cursor-pointer rounded-full bg-gray-800 bg-[length:0.8em_0.8em] bg-no-repeat transition-opacity content-[''];
        }
    }
    .swiper-button-prev {
        @apply left-0 bg-gradient-to-r from-white from-70% to-transparent pr-6;
        &::before {
            background-image: url('@/assets/icons/chevron-left.svg');
            @apply bg-[left_0.35em_top_0.3em];
        }
    }
    .left-arrow-hidden .swiper-button-prev {
        @apply hidden;
    }
    .swiper-button-next {
        @apply right-0 bg-gradient-to-r from-transparent to-white to-30% pl-6;
        &::before {
            background-image: url('@/assets/icons/chevron-right.svg');
            @apply bg-[right_0.3em_top_0.3em];
        }
    }
</style>
